import React, { useState, useEffect, useCallback, useRef, forwardRef, useImperativeHandle } from 'react';
import { Container, Spinner } from 'react-bootstrap';
import { generateKeyBetween } from 'fractional-indexing';
import { makeRESTApiRequest } from '../../../helpers/api';
import { ENDPOINTS } from '../../../helpers/constants';
import { toast } from 'sonner';
import isEqual from 'lodash/isEqual';
import { useHistory } from 'react-router-dom';
import useMunicipalityCounty from '../../../hooks/useMunicipalityCounty';
import AlertModal from '../../AlertModal';
import InstructionHeader from './InstructionHeader';
import InstructionTimeline from './InstructionTimeline';
import StepModal from './StepModal';
import FileAttachmentsModal from './FileAttachmentsModal';

const isPersistedStepId = id => id && !String(id).startsWith('temp-');

const assignStepOrders = stepsList => {
  let prevOrder = null;
  return stepsList.map(step => {
    const order = generateKeyBetween(prevOrder, null);
    prevOrder = order;
    return { ...step, order };
  });
};

const InstructionBuilder = forwardRef(({ onNavigateToTab }, ref) => {
  const [selectedMunicipality, setSelectedMunicipality] = useState('');
  const [steps, setSteps] = useState([]);
  const [initialSteps, setInitialSteps] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [activeTab, setActiveTab] = useState('All');

  // Modal states
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);
  const [showFileModal, setShowFileModal] = useState(false);
  const [fileModalStep, setFileModalStep] = useState(null);
  const [deleteStepCandidate, setDeleteStepCandidate] = useState(null);
  const [hideStepCandidate, setHideStepCandidate] = useState(null);

  // Unsaved changes
  const [showUnsavedChangesAlert, setShowUnsavedChangesAlert] = useState(false);
  const [pendingLeaveAction, setPendingLeaveAction] = useState(null);
  const showUnsavedChangesAlertRef = useRef(false);
  const unblockRef = useRef(null);

  const { records: countyMunicipalityRecords, municipalityOptions, allCities } = useMunicipalityCounty();
  const loadRequestIdRef = useRef(0);
  const hasInitializedMunicipality = useRef(false);
  const history = useHistory();

  useEffect(() => {
    if (hasInitializedMunicipality.current || allCities.length === 0) return;

    const defaultMunicipality = allCities[0] || '';
    if (defaultMunicipality) {
      setSelectedMunicipality(prev => prev || defaultMunicipality);
    }

    hasInitializedMunicipality.current = true;
  }, [allCities]);

  // Derive county from municipality
  const selectedCounty = React.useMemo(() => {
    if (!selectedMunicipality || !Array.isArray(countyMunicipalityRecords)) return '';
    const found = countyMunicipalityRecords.find(item => item.city === selectedMunicipality);
    return found?.county || '';
  }, [selectedMunicipality, countyMunicipalityRecords]);

  // Check for unsaved changes
  const hasUnsavedChanges = React.useMemo(() => !isEqual(steps, initialSteps), [steps, initialSteps]);

  useEffect(() => {
    showUnsavedChangesAlertRef.current = showUnsavedChangesAlert;
  }, [showUnsavedChangesAlert]);

  // Browser refresh/close warning
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    const handleBeforeUnload = e => {
      if (hasUnsavedChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasUnsavedChanges]);

  // Navigation guard
  useEffect(() => {
    if (!hasUnsavedChanges) return;

    unblockRef.current = history.block(tx => {
      if (showUnsavedChangesAlertRef.current) return false;

      setPendingLeaveAction({
        type: 'route',
        retry: tx?.retry,
        location: tx?.location
      });
      setShowUnsavedChangesAlert(true);
      return false;
    });

    return () => {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
    };
  }, [history, hasUnsavedChanges]);

  // Load instruction steps
  useEffect(() => {
    if (!selectedMunicipality || !selectedCounty) {
      setSteps([]);
      setInitialSteps([]);
      setLoading(false);
      return;
    }

    const requestId = ++loadRequestIdRef.current;
    setLoading(true);
    setSteps([]);

    makeRESTApiRequest({
      endpoint: ENDPOINTS.INSTRUCTION_BUILDER_STEPS({
        county: selectedCounty,
        municipality: selectedMunicipality,
        showHidden: true
      }),
      method: 'GET'
    })
      .then(({ response, error }) => {
        if (requestId !== loadRequestIdRef.current) return;

        if (error || !response?.data || !Array.isArray(response.data.steps)) {
          setSteps([]);
          setInitialSteps([]);
          return;
        }

        const loadedSteps = response.data.steps;
        setSteps(loadedSteps);
        setInitialSteps(loadedSteps);
      })
      .catch(err => {
        if (requestId !== loadRequestIdRef.current) return;
        console.error('Error loading instruction steps:', err);
        toast.error('Failed to load instruction steps');
        setSteps([]);
        setInitialSteps([]);
      })
      .finally(() => {
        if (requestId === loadRequestIdRef.current) {
          setLoading(false);
        }
      });
  }, [selectedMunicipality, selectedCounty]);

  // Filter steps by active tab and hidden state
  const visibleSteps = React.useMemo(() => {
    let list = steps;
    if (!showHidden) {
      list = list.filter(step => !step.isHidden);
    }
    if (activeTab !== 'All') {
      list = list.filter(step => step.tab === activeTab);
    }
    return list;
  }, [steps, activeTab, showHidden]);

  const handleSave = async ({ silent = false } = {}) => {
    if (!selectedMunicipality || !selectedCounty) {
      if (!silent) toast.error('Please select a municipality');
      return { ok: false };
    }

    setSaving(true);
    try {
      const orderedSteps = assignStepOrders(steps);
      const stepsPayload = orderedSteps.map(step => ({
        id: isPersistedStepId(step._id) ? step._id : undefined,
        scope: step.scope,
        tab: step.tab,
        title: step.title || '',
        description: step.description || '',
        instruction: step.instruction || '',
        order: step.order,
        isHidden: Boolean(step.isHidden)
      }));

      const { response, error } = await makeRESTApiRequest({
        endpoint: ENDPOINTS.INSTRUCTION_BUILDER_STEPS_UPDATE,
        method: 'POST',
        requestBody: {
          county: selectedCounty,
          municipality: selectedMunicipality,
          steps: stepsPayload
        }
      });

      if (error) {
        if (!silent) toast.error(error || 'Failed to save steps');
        return { ok: false };
      }

      const savedSteps = response?.data?.steps || [];
      setSteps(savedSteps);
      setInitialSteps(savedSteps);
      if (!silent) toast.success('Changes saved successfully');
      return { ok: true };
    } catch (err) {
      console.error('Error saving steps:', err);
      if (!silent) toast.error('Failed to save steps');
      return { ok: false };
    } finally {
      setSaving(false);
    }
  };

  const handleCancelLeave = () => {
    setShowUnsavedChangesAlert(false);
    setPendingLeaveAction(null);
  };

  const handleDiscardChanges = () => {
    const action = pendingLeaveAction;

    setShowUnsavedChangesAlert(false);
    setPendingLeaveAction(null);
    setSteps(initialSteps);

    if (!action) return;

    if (action.type === 'municipality') {
      setSelectedMunicipality(action.nextMunicipality);
      return;
    }

    if (action.type === 'back-office-tab') {
      onNavigateToTab?.(action.nextTabId);
      return;
    }

    if (action.type === 'show-hidden') {
      setShowHidden(action.nextShowHidden);
      return;
    }

    if (action.type === 'route') {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      if (action.retry) action.retry();
      else if (action.location) {
        history.push(`${action.location.pathname || ''}${action.location.search || ''}`);
      }
    }
  };

  const handleSaveAndContinue = async () => {
    const action = pendingLeaveAction;

    setShowUnsavedChangesAlert(false);
    setPendingLeaveAction(null);

    const saveResult = await handleSave({ silent: true });
    if (!saveResult?.ok) {
      setShowUnsavedChangesAlert(true);
      setPendingLeaveAction(action);
      toast.error('Failed to save changes');
      return;
    }

    if (!action) return;

    if (action.type === 'municipality') {
      setSelectedMunicipality(action.nextMunicipality);
      return;
    }

    if (action.type === 'back-office-tab') {
      onNavigateToTab?.(action.nextTabId);
      return;
    }

    if (action.type === 'show-hidden') {
      setShowHidden(action.nextShowHidden);
      return;
    }

    if (action.type === 'route') {
      if (unblockRef.current) {
        unblockRef.current();
        unblockRef.current = null;
      }
      if (action.retry) action.retry();
      else if (action.location) {
        history.push(`${action.location.pathname || ''}${action.location.search || ''}`);
      }
    }
  };

  const handleShowHiddenChange = value => {
    if (value === showHidden) return;

    if (!hasUnsavedChanges) {
      setShowHidden(value);
      return;
    }

    if (showUnsavedChangesAlertRef.current) return;

    setPendingLeaveAction({ type: 'show-hidden', nextShowHidden: value });
    setShowUnsavedChangesAlert(true);
  };

  const handleAddStep = () => {
    setEditingStep(null);
    setShowStepModal(true);
  };

  const handleEditStep = step => {
    setEditingStep(step);
    setShowStepModal(true);
  };

  const handleStepModalSave = (formData, existingStep) => {
    if (existingStep) {
      setSteps(prev =>
        prev.map(step =>
          step._id === existingStep._id
            ? {
                ...step,
                title: formData.title || '',
                description: formData.description || '',
                tab: formData.tab,
                instruction: formData.instruction || ''
              }
            : step
        )
      );
      return;
    }

    const lastOrder = steps.length > 0 ? steps[steps.length - 1].order : null;
    const newStep = {
      _id: `temp-${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 9)}`,
      tab: formData.tab,
      title: formData.title || '',
      description: formData.description || '',
      instruction: formData.instruction || '',
      scope: formData.scope,
      county: selectedCounty,
      municipality: formData.scope === 'municipality' ? selectedMunicipality : undefined,
      order: generateKeyBetween(lastOrder, null),
      files: [],
      isHidden: false
    };

    setSteps(prev => [...prev, newStep]);
  };

  const handleDeleteStep = step => {
    setDeleteStepCandidate(step);
  };

  const handleHideStep = step => {
    if (step.scope !== 'county') return;

    if (step.isHidden) {
      setSteps(prev => prev.map(item => (item._id === step._id ? { ...item, isHidden: false } : item)));
      return;
    }

    setHideStepCandidate(step);
  };

  const handleConfirmDeleteStep = () => {
    if (!deleteStepCandidate) return;

    const step = deleteStepCandidate;
    setDeleteStepCandidate(null);
    setSteps(prev => prev.filter(item => item._id !== step._id));
  };

  const handleConfirmHideStep = () => {
    if (!hideStepCandidate) return;

    const step = hideStepCandidate;
    setHideStepCandidate(null);
    setSteps(prev => prev.map(item => (item._id === step._id ? { ...item, isHidden: true } : item)));
  };

  const handleManageFiles = step => {
    if (!isPersistedStepId(step._id)) {
      toast.error('Save the step before managing attachments');
      return;
    }
    setFileModalStep(step);
    setShowFileModal(true);
  };

  const handleStepFilesChange = (stepId, files) => {
    const updateFiles = prev => prev.map(step => (step._id === stepId ? { ...step, files } : step));
    setSteps(updateFiles);
    setInitialSteps(updateFiles);
    setFileModalStep(prev => (prev?._id === stepId ? { ...prev, files } : prev));
  };

  const handleReorder = reorderedVisibleSteps => {
    setSteps(prev => {
      let merged;
      if (activeTab === 'All' && showHidden) {
        merged = reorderedVisibleSteps;
      } else {
        const visibleIds = new Set(reorderedVisibleSteps.map(step => step._id));
        const queue = [...reorderedVisibleSteps];
        merged = prev.map(step => (visibleIds.has(step._id) ? queue.shift() : step));
      }
      return assignStepOrders(merged);
    });
  };

  // Municipality change handler
  const requestMunicipalityChange = useCallback(
    nextMunicipality => {
      if (nextMunicipality === selectedMunicipality) return;

      if (!hasUnsavedChanges) {
        setSelectedMunicipality(nextMunicipality);
        return;
      }

      if (showUnsavedChangesAlertRef.current) return;

      setPendingLeaveAction({ type: 'municipality', nextMunicipality });
      setShowUnsavedChangesAlert(true);
    },
    [selectedMunicipality, hasUnsavedChanges]
  );

  // Tab change handler
  const requestBackOfficeTabChange = useCallback(
    nextTabId => {
      if (!hasUnsavedChanges) {
        return false;
      }

      if (showUnsavedChangesAlertRef.current) {
        return true;
      }

      setPendingLeaveAction({ type: 'back-office-tab', nextTabId });
      setShowUnsavedChangesAlert(true);
      return true;
    },
    [hasUnsavedChanges]
  );

  useImperativeHandle(
    ref,
    () => ({
      requestBackOfficeTabChange
    }),
    [requestBackOfficeTabChange]
  );

  return (
    <Container fluid className="h-100 py-3 px-md-3">
      <div className="d-flex flex-column h-100 px-4">
        <div>
          <InstructionHeader
            selectedCounty={selectedCounty}
            selectedMunicipality={selectedMunicipality}
            municipalityOptions={municipalityOptions}
            onMunicipalityChange={requestMunicipalityChange}
            showHidden={showHidden}
            onShowHiddenChange={handleShowHiddenChange}
            onAddStep={handleAddStep}
            onSave={handleSave}
            saving={saving}
            hasUnsavedChanges={hasUnsavedChanges}
            activeTab={activeTab}
            onTabChange={setActiveTab}
            allSteps={steps}
          />

          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
              <Spinner animation="border" />
            </div>
          ) : selectedMunicipality ? (
            <InstructionTimeline
              steps={visibleSteps}
              onEditStep={handleEditStep}
              onDeleteStep={handleDeleteStep}
              onHideStep={handleHideStep}
              onManageFiles={handleManageFiles}
              onReorder={handleReorder}
              selectedMunicipality={selectedMunicipality}
              selectedCounty={selectedCounty}
            />
          ) : (
            <div className="d-flex flex-column justify-content-center align-items-center h-100">
              <p className="xmidFont text-muted">Select a municipality to view and edit instructions</p>
            </div>
          )}
        </div>
      </div>

      <StepModal
        show={showStepModal}
        onHide={() => setShowStepModal(false)}
        step={editingStep}
        county={selectedCounty}
        municipality={selectedMunicipality}
        onSaveStep={handleStepModalSave}
      />

      <FileAttachmentsModal
        show={showFileModal}
        onHide={() => setShowFileModal(false)}
        step={fileModalStep}
        onFilesChange={handleStepFilesChange}
      />

      <AlertModal
        show={deleteStepCandidate !== null}
        onHide={() => setDeleteStepCandidate(null)}
        onDismissClick={() => setDeleteStepCandidate(null)}
        onContinueClick={handleConfirmDeleteStep}
        alertText={
          deleteStepCandidate?.scope === 'county'
            ? 'Are you sure you want to delete this county step? This will remove it for all municipalities in this county.'
            : 'Are you sure you want to delete this step? This action cannot be undone!'
        }
        dismissButtonText="Cancel"
        dismissButtonVariant="secondary"
        continueButtonText="Delete"
        continueButtonVariant="danger"
      />

      <AlertModal
        show={hideStepCandidate !== null}
        onHide={() => setHideStepCandidate(null)}
        onDismissClick={() => setHideStepCandidate(null)}
        onContinueClick={handleConfirmHideStep}
        alertText="Are you sure you want to hide this county step for this municipality?"
        dismissButtonText="Cancel"
        dismissButtonVariant="secondary"
        continueButtonText="Hide"
        continueButtonVariant="warning"
      />

      <AlertModal
        show={showUnsavedChangesAlert}
        onHide={handleCancelLeave}
        onDismissClick={handleDiscardChanges}
        onContinueClick={handleSaveAndContinue}
        alertText="You have unsaved changes. Do you want to save them before continuing?"
        dismissButtonText="Discard Changes"
        dismissButtonVariant="danger"
        continueButtonText="Save Changes"
        continueButtonVariant="success"
        showProgress={saving}
        progressText="Saving..."
      />
    </Container>
  );
});

InstructionBuilder.displayName = 'InstructionBuilder';

export default InstructionBuilder;
